/*
 * DAC: fills the Blog posts, Podcasts and Pages groups on the search results page (sections/main-search-content.liquid).
 *
 * Each content type is fetched with its own uncapped search (type=article / type=page) through the Section Rendering
 * API. Blog posts and podcasts come back from the same article search, so results are queued per group and each
 * "Show more" pulls further result pages until that group has enough to show (or the results run out).
 */
if (!customElements.get('search-content-results')) {
  class SearchContentResults extends HTMLElement {
    // Stop chasing one group through a long run of results that belong to the other
    static MAX_FETCHES_PER_LOAD = 4;

    connectedCallback() {
      this.itemsPerLoad = parseInt(this.dataset.itemsPerLoad, 10) || 6;
      this.statusElement = this.querySelector('[data-search-content-status]');

      this.groups = {};
      this.querySelectorAll('[data-search-content-group]').forEach((element) => {
        const name = element.dataset.searchContentGroup;
        const button = element.querySelector('[data-search-content-more]');

        this.groups[name] = {
          element,
          list: element.querySelector('[data-search-content-list]'),
          button,
          queue: [],
          source: name === 'page' ? 'page' : 'article',
        };
        button.addEventListener('click', () => this.showMore(name));
      });

      this.sources = {};
      Object.values(this.groups).forEach((group) => {
        this.sources[group.source] = this.sources[group.source] || { page: 0, pages: 1, request: null };
      });

      Object.keys(this.groups).forEach((name) => this.showMore(name, { announce: false }));
    }

    async showMore(name, { announce = true } = {}) {
      const group = this.groups[name];
      const source = this.sources[group.source];
      let fetches = 0;

      group.button.disabled = true;
      this.setAttribute('loading', '');

      try {
        while (
          group.queue.length < this.itemsPerLoad &&
          source.page < source.pages &&
          fetches < SearchContentResults.MAX_FETCHES_PER_LOAD
        ) {
          await this.fetchNextPage(group.source);
          fetches++;
        }
      } catch (error) {
        console.error(error);
      }

      const items = group.queue.splice(0, this.itemsPerLoad);
      items.forEach((item) => group.list.appendChild(item));

      if (group.list.children.length > 0) group.element.hidden = false;
      group.button.hidden = true;
      group.button.disabled = false;
      if (!this.hasPendingRequest()) this.removeAttribute('loading');

      if (announce && items.length > 0) {
        this.statusElement.textContent = `${items.length} more ${group.element.querySelector('h2').textContent.trim()}`;
        items[0].querySelector('a')?.focus({ preventScroll: true });
      }

      await this.revealButtonIfMore(name);
    }

    // Only offer "Show more" once a further result for this group is actually queued. More article pages can hold
    // nothing but the other group's results, so keep looking in the background until one turns up or the pages run out.
    async revealButtonIfMore(name) {
      const group = this.groups[name];
      const source = this.sources[group.source];

      try {
        while (group.queue.length === 0 && source.page < source.pages) {
          await this.fetchNextPage(group.source);
        }
      } catch (error) {
        console.error(error);
      }

      if (!this.hasPendingRequest()) this.removeAttribute('loading');
      if (group.queue.length === 0) return;

      // The first load ran out of fetches before reaching this group's results
      if (group.list.children.length === 0) {
        group.queue.splice(0, this.itemsPerLoad).forEach((item) => group.list.appendChild(item));
        group.element.hidden = false;
        if (group.queue.length === 0) return this.revealButtonIfMore(name);
      }

      group.button.hidden = false;
    }

    // Blog posts and podcasts share the article source; a request already in flight is reused rather than repeated
    fetchNextPage(sourceName) {
      const source = this.sources[sourceName];
      if (source.request) return source.request;

      const params = new URLSearchParams({
        q: this.dataset.terms,
        type: sourceName,
        'options[prefix]': 'last',
        page: source.page + 1,
        section_id: this.dataset.sectionId,
      });

      source.request = fetch(`${this.dataset.searchUrl}?${params.toString()}`)
        .then((response) => {
          if (!response.ok) throw new Error(`Search content request failed: ${response.status}`);
          return response.text();
        })
        .then((text) => {
          const template = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector('template[data-search-content-response]');

          source.page++;
          if (!template) {
            source.pages = source.page;
            return;
          }

          source.pages = parseInt(template.dataset.pages, 10) || source.page;
          template.content.querySelectorAll('[data-search-content-item]').forEach((item) => {
            const group = this.groups[item.dataset.searchContentItem];
            if (group) group.queue.push(document.importNode(item, true));
          });
        })
        .finally(() => {
          source.request = null;
        });

      return source.request;
    }

    hasPendingRequest() {
      return Object.values(this.sources).some((source) => source.request);
    }
  }

  customElements.define('search-content-results', SearchContentResults);
}
